import { useQuery } from '@tanstack/react-query';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import { ErrorState } from '@heyroot/ui/primitives/ErrorState';
import { Skeleton } from '@heyroot/ui/primitives/Skeleton';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
} from '@heyroot/ui/primitives/Table';
import { fetchStuckTenantWorklist } from '../../lib/onboarding-funnel-api.js';

export function StuckTenantWorklist() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['onboarding-funnel-stuck-tenants'],
    queryFn: () => fetchStuckTenantWorklist({ cursor: null, limit: 50 }),
  });

  if (isLoading) return <Skeleton data-testid="stuck-worklist-skeleton" />;
  if (isError) {
    return (
      <ErrorState
        description="Couldn't load the stuck-tenant worklist."
        onRetry={() => void refetch()}
      />
    );
  }

  const tenants = data?.tenants ?? [];
  if (tenants.length === 0) {
    return (
      <EmptyState
        title="No stuck tenants right now."
        description="No live tenant is overdue for a first sale, and no pre-live tenant is stalled on a blocked step."
      />
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Table aria-label="Stuck tenants">
        <TableHead>
          <TableRow>
            <TableHeaderCell>Business</TableHeaderCell>
            <TableHeaderCell>Reason(s)</TableHeaderCell>
            <TableHeaderCell>Days since created</TableHeaderCell>
            <TableHeaderCell>Last activity</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tenants.map((t) => (
            <TableRow key={t.tenantId}>
              <TableCell>
                <a href={`/tenants/${encodeURIComponent(t.tenantId)}?tab=onboarding`}>
                  {t.businessName}
                </a>
              </TableCell>
              <TableCell>
                <div className="flex gap-1">
                  {t.reasonCodes.map((code) => (
                    <Badge key={code} variant="warning">
                      {code}
                    </Badge>
                  ))}
                </div>
              </TableCell>
              <TableCell>{t.daysSinceCreated}</TableCell>
              <TableCell>
                {t.lastActivityAt ? new Date(t.lastActivityAt).toISOString() : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="text-body-sm text-muted">
        Showing top {tenants.length}
        {data?.nextCursor ? ' (more available)' : ''}.
      </p>
    </div>
  );
}
